// Defines the main application component, including routing and layout.
import React from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { AppBar, Box, Toolbar, Typography, Container, IconButton, Tooltip } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import listIcon from './assets/icons/list.svg';
import boltAssemblyIcon from './assets/icons/bolt-assembly.svg';
import piledFoundationIcon from './assets/icons/piled_foundation.svg';
import compositeColumnIcon from './assets/icons/composite_column.svg';
import slabCalculatorIcon from './assets/icons/slab_calculator.svg';
import trussFrameIcon from './assets/icons/Truss_Frame.svg';
import Dashboard from './components/Dashboard/Dashboard';
import CrossSectionsTable from './components/CrossSections/CrossSectionsTable';
import BeamFormulaCalculator from './components/BeamFormulae/BeamFormulaCalculator';
import FoundationAnalysis from './components/Foundations/FoundationAnalysis';
import CompositeColumnAnalysis from './components/CompositeColumns/CompositeColumnAnalysis';
import ConsoleCalculations from './components/ConsoleCalculations/ConsoleCalculations';
import { BeamProvider } from './context/BeamContext';
import SlabAnalysis from './components/slab_calculator/SlabAnalysis';
import ChangelogPage from './components/Changelog/ChangelogPage';
import BoltTables from './components/BoltTables/BoltTables';
import TrussCalculator from './components/TrussCalculator/TrussCalculator';
import { SUITE_VERSION } from './config/appVersions';
import { WorkspaceProvider } from './context/WorkspaceContext';
import WorkspaceLoginDialog from './components/shared/WorkspaceLoginDialog';
import WorkspaceToolbarButton from './components/shared/WorkspaceToolbarButton';
import MapIcon from '@mui/icons-material/Map';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SitePlanner from './components/SitePlanner/SitePlanner';
import FiSnowDigitizer from './components/FiSnowDigitizer/FiSnowDigitizer';

const NavIcon = ({ src, alt }) => (
  <img src={src} alt={alt} width={24} height={24} style={{ display: 'block' }} />
);

function App() {
  return (
    <WorkspaceProvider>
    <div className="App">
      <AppBar position="static">
        <Toolbar>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Typography
              variant="h6"
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                flexShrink: 0,
              }}
            >
              Dashboard
            </Typography>
            <Typography
              component="span"
              variant="body2"
              sx={{
                opacity: 0.88,
                fontWeight: 400,
                lineHeight: 1.4,
              }}
            >
              · Alpha {SUITE_VERSION}, not for production
            </Typography>
          </Box>
          <WorkspaceToolbarButton />
          <Tooltip title="Changelog and versions">
            <IconButton color="inherit" component={Link} to="/changelog">
              <ArticleOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="List of cross-sections">
            <IconButton color="inherit" component={Link} to="/cross-sections">
              <NavIcon src={listIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Bolt, nut, and washer reference tables">
            <IconButton color="inherit" component={Link} to="/bolts">
              <NavIcon src={boltAssemblyIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Calculate beam deflections and forces">
            <IconButton color="inherit" component={Link} to="/formulae">
              <CalculateIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="EN 1997 Foundation calculation tool">
            <IconButton color="inherit" component={Link} to="/foundations">
              <NavIcon src={piledFoundationIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Composite column design (EN 1994)">
            <IconButton color="inherit" component={Link} to="/composite-columns">
              <NavIcon src={compositeColumnIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Concrete corbel and neoprene pad calculation tool">
            <IconButton color="inherit" component={Link} to="/console">
              <EngineeringIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Precast and hollow core slab analysis">
            <IconButton color="inherit" component={Link} to="/slab_calculator">
              <NavIcon src={slabCalculatorIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Truss and portal frame (steel), EC3 checks">
            <IconButton color="inherit" component={Link} to="/truss">
              <NavIcon src={trussFrameIcon} alt="" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Site Planner (setbacks, DXF tracing, layout optimizer)">
            <IconButton color="inherit" component={Link} to="/site-planner">
              <MapIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="FI snow map (YM 6/16) — view / probe">
            <IconButton color="inherit" component={Link} to="/fi-snow-digitizer">
              <AcUnitIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>
      
      <Container maxWidth={false} sx={{ mt: 4 }}>
        <BeamProvider>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/bolts" element={<BoltTables />} />
            <Route path="/cross-sections" element={<CrossSectionsTable />} />
            <Route path="/xsec" element={<CrossSectionsTable />} />
            <Route path="/crosssections" element={<CrossSectionsTable />} />
            <Route path="/table" element={<CrossSectionsTable />} />
            <Route path="/formulae" element={<BeamFormulaCalculator />} />
            <Route path="/formulas" element={<BeamFormulaCalculator />} />
            <Route path="/truss" element={<TrussCalculator />} />
            <Route path="/foundations" element={<FoundationAnalysis />} />
            <Route path="/composite-columns" element={<CompositeColumnAnalysis />} />
            <Route path="/console" element={<ConsoleCalculations />} />
            <Route path="/slab_calculator" element={<SlabAnalysis />} />
            <Route path="/changelog" element={<ChangelogPage />} />
            <Route path="/site-planner" element={<SitePlanner />} />
            <Route path="/fi-snow-digitizer" element={<FiSnowDigitizer />} />
          </Routes>
        </BeamProvider>
      </Container>
      <WorkspaceLoginDialog />
    </div>
    </WorkspaceProvider>
  );
}

export default App;